import { useMemo } from 'react';
import { useAppSelector } from '../ducks/hooks';
import { EIncentiveType, EUserRole, TCoreParticipation } from '../ducks/api-ducks/participation-details-duck/types-participation-details';

export const useSelectedParticipation = () => {
  const filters = useAppSelector((state) => state.filters);
  const participationDetails = useAppSelector((state) => state.participationDetails);

  return useMemo(
    () =>
      participationDetails?.coreParticipations?.find(
        (participation: TCoreParticipation) => filters.participationId === participation.participationId
      ),
    [participationDetails?.coreParticipations, filters.participationId]
  );
};

export const useIncentiveTypeForParticipation = (): EIncentiveType | undefined => {
  const selectedParticipation = useSelectedParticipation();
  return selectedParticipation?.payMixDetails?.incentiveType;
};

export const useIsSinglePaylinePlan = (): boolean => {
  const selectedParticipation = useSelectedParticipation();
  return !!selectedParticipation?.isSinglePaylinePlan;
};

export const isNPM = (participation?: TCoreParticipation | null): boolean => {
  if (!participation) {
    return false;
  }
  return participation.userRole === EUserRole.NonParticipantManager;
};

export const useIsUBI = () => useIncentiveTypeForParticipation() === EIncentiveType.UBI;
export const useIsRBI = () => useIncentiveTypeForParticipation() === EIncentiveType.RBI;
export const useIsNPM = (): boolean => {
  const selectedParticipation = useSelectedParticipation();
  return isNPM(selectedParticipation);
};

export const useQuotaPhrase = () => {
  const incentiveType = useIncentiveTypeForParticipation();
  return useMemo(() => incentiveType === EIncentiveType.RBI || typeof incentiveType === 'undefined', [incentiveType]);
};

// If a user is a manager in its latest participation, it is considered a manager.
export const useIsManager = () => {
  const participationDetails = useAppSelector((state) => state.participationDetails);
  const sortedParticipations = useMemo(() => {
    if (!participationDetails) {
      return [];
    }
    if (!participationDetails.coreParticipations) {
      return [];
    }
    return [...participationDetails.coreParticipations].sort((a, b) => b.fiscalYearId - a.fiscalYearId);
  }, [participationDetails]);
  return useMemo(() => {
    if (!sortedParticipations || sortedParticipations.length === 0) {
      return false;
    }
    const latestParticipation = sortedParticipations[0];
    return latestParticipation.userRole === EUserRole.ParticipantManager || latestParticipation.userRole === EUserRole.NonParticipantManager;
  }, [sortedParticipations]);
};
